import { useMemo, useCallback } from "react";
import { Company } from "@/types/company/company.types";
import { useLanguage } from "@/contexts/LanguageContext";
import { normalizeText } from "@/utils/textFormatter";

interface UseCompanyFiltersProps {
  companies: Company[];
  searchTerm: string;
  showArchived: boolean;
  showOnlyRegistered?: boolean;
  showOnlyNotRegistered?: boolean;
  companyFilters: string[];
  activityTypeFilters: string[];
  rockTypeFilters: string[];
  productTypeFilters: string[];
}

export const useCompanyFilters = ({
  companies,
  searchTerm,
  showArchived,
  showOnlyRegistered = false,
  showOnlyNotRegistered = false,
  companyFilters,
  activityTypeFilters,
  rockTypeFilters,
  productTypeFilters,
}: UseCompanyFiltersProps) => {
  const { t } = useLanguage();

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const normalizedSearchTerm = normalizeText(searchTerm);
      const normalizedCompanyName = normalizeText(company.name);
      const normalizedActivityType = normalizeText(company.activityType ?? "");
      const normalizedRockType = normalizeText(company.rockType ?? "");
      const normalizedProductType = normalizeText(company.productType ?? "");
      const normalizedAssociateNumber = normalizeText(company.associateNumber?.toString() ?? "");
      const normalizedBasinName = normalizeText(company.basinName ?? "");
      const normalizedWeatherStationName = normalizeText(company.weatherStationName ?? "");

      const translatedActivityType = company.activityType 
        ? t(`activityTypes.${company.activityType.toLowerCase().replace(/ /g, "_")}`)
        : "";
      const normalizedTranslatedActivityType = normalizeText(translatedActivityType);

      const matchesSearch =
        normalizedCompanyName.includes(normalizedSearchTerm) ||
        normalizedActivityType.includes(normalizedSearchTerm) ||
        normalizedTranslatedActivityType.includes(normalizedSearchTerm) ||
        normalizedRockType.includes(normalizedSearchTerm) ||
        normalizedProductType.includes(normalizedSearchTerm) ||
        normalizedAssociateNumber.includes(normalizedSearchTerm) ||
        normalizedBasinName.includes(normalizedSearchTerm) ||
        normalizedWeatherStationName.includes(normalizedSearchTerm);

      const matchesArchived = showArchived ? company.isArchived ?? false : !(company.isArchived ?? false);

      // Registration filter logic
      let matchesRegistration = true;
      if (showOnlyRegistered) {
        matchesRegistration = company.accountState?.isRegistered ?? false;
      } else if (showOnlyNotRegistered) {
        matchesRegistration = !(company.accountState?.isRegistered ?? false);
      }
      // If neither showOnlyRegistered nor showOnlyNotRegistered is true, show all (matchesRegistration = true)

      const matchesCompanyFilter = companyFilters.length === 0 || companyFilters.includes(company.name);
      const matchesActivityTypeFilter =
        activityTypeFilters.length === 0 || activityTypeFilters.includes(company.activityType ?? "");
      const matchesRockTypeFilter = rockTypeFilters.length === 0 || rockTypeFilters.includes(company.rockType ?? "");
      const matchesProductTypeFilter =
        productTypeFilters.length === 0 || productTypeFilters.includes(company.productType ?? "");

      return (
        matchesSearch &&
        matchesArchived &&
        matchesRegistration &&
        matchesCompanyFilter &&
        matchesActivityTypeFilter &&
        matchesRockTypeFilter &&
        matchesProductTypeFilter
      );
    });
  }, [
    companies,
    searchTerm,
    showArchived,
    showOnlyRegistered,
    showOnlyNotRegistered,
    companyFilters,
    activityTypeFilters,
    rockTypeFilters,
    productTypeFilters,
  ]);

  const getUniqueOptions = useCallback(
    (key: keyof Company) => {
      const filteredByOtherFilters = companies.filter((company) => {
        const normalizedSearchTerm = normalizeText(searchTerm);
        const normalizedCompanyName = normalizeText(company.name);
        const normalizedActivityType = normalizeText(company.activityType ?? "");
        const normalizedRockType = normalizeText(company.rockType ?? "");
        const normalizedProductType = normalizeText(company.productType ?? "");
        const normalizedAssociateNumber = normalizeText(company.associateNumber?.toString() ?? "");
        const normalizedBasinName = normalizeText(company.basinName ?? "");
        const normalizedWeatherStationName = normalizeText(company.weatherStationName ?? "");

        const translatedActivityType = company.activityType 
          ? t(`activityTypes.${company.activityType.toLowerCase().replace(/ /g, "_")}`)
          : "";
        const normalizedTranslatedActivityType = normalizeText(translatedActivityType);

        const matchesSearch =
          normalizedCompanyName.includes(normalizedSearchTerm) ||
          normalizedActivityType.includes(normalizedSearchTerm) ||
          normalizedTranslatedActivityType.includes(normalizedSearchTerm) ||
          normalizedRockType.includes(normalizedSearchTerm) ||
          normalizedProductType.includes(normalizedSearchTerm) ||
          normalizedAssociateNumber.includes(normalizedSearchTerm) ||
          normalizedBasinName.includes(normalizedSearchTerm) ||
          normalizedWeatherStationName.includes(normalizedSearchTerm);

        const matchesArchived = showArchived ? company.isArchived ?? false : !(company.isArchived ?? false);

        let matchesRegistration = true;
        if (showOnlyRegistered) {
          matchesRegistration = company.accountState?.isRegistered ?? false;
        } else if (showOnlyNotRegistered) {
          matchesRegistration = !(company.accountState?.isRegistered ?? false);
        }

        const matchesCompanyFilter =
          key === "name" || companyFilters.length === 0 || companyFilters.includes(company.name);
        const matchesActivityTypeFilter =
          key === "activityType" ||
          activityTypeFilters.length === 0 ||
          activityTypeFilters.includes(company.activityType ?? "");
        const matchesRockTypeFilter =
          key === "rockType" || rockTypeFilters.length === 0 || rockTypeFilters.includes(company.rockType ?? "");
        const matchesProductTypeFilter =
          key === "productType" ||
          productTypeFilters.length === 0 ||
          productTypeFilters.includes(company.productType ?? "");

        return (
          matchesSearch &&
          matchesArchived &&
          matchesRegistration &&
          matchesCompanyFilter &&
          matchesActivityTypeFilter &&
          matchesRockTypeFilter &&
          matchesProductTypeFilter
        );
      });

      const uniqueValues = Array.from(new Set(filteredByOtherFilters.map((company) => company[key])));

      return uniqueValues.filter(Boolean).map((value) => {
        // Special case for activity type to use translations
        if (key === "activityType" && typeof value === "string") {
          const translationKey = `activityTypes.${value.toLowerCase().replace(/ /g, "_")}`;
          const translatedValue = t(translationKey);
          // If translation exists, use it, otherwise use the original value
          return {
            label: translatedValue !== translationKey ? translatedValue : (value as string),
            value: value as string,
          };
        }

        return { label: value as string, value: value as string };
      });
    },
    [
      companies,
      searchTerm,
      showArchived,
      showOnlyRegistered,
      showOnlyNotRegistered,
      companyFilters,
      activityTypeFilters,
      rockTypeFilters,
      productTypeFilters,
      t,
    ]
  );

  return {
    filteredCompanies,
    getUniqueOptions,
  };
};
